import React, { useState } from 'react';
import { usePlayerSimilarity } from '../lib/player-similarity.context';
import { SimilarPlayer } from '../types/player-similarity';
import { Button } from './ui/button';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';

export const SimilarPlayersResults: React.FC = () => {
  const { 
    similarPlayers, 
    isSearchingSimilar, 
    error, 
    selectedPlayer, 
    isCriteriaValid,
    similarityCriteria,
    clearSearch
  } = usePlayerSimilarity();

  // State to track which player's details are expanded
  const [expandedPlayerId, setExpandedPlayerId] = useState<string | null>(null);

  // Helper function to convert meters to feet and inches
  const metersToFeetAndInches = (heightStr: string | null | undefined): string => {
    if (!heightStr) {
      return 'N/A';
    }
    
    try {
      // Convert string like "1,90m" to number 1.90 by removing 'm' and replacing comma
      const m = parseFloat(heightStr.replace('m', '').replace(',', '.'));
      if (isNaN(m)) {
        return 'N/A';
      }
      const totalInches = m * 39.3701;
      const feet = Math.floor(totalInches / 12);
      const inches = Math.round(totalInches % 12);
      return `${feet}'${inches}"`;
    } catch (error) {
      console.error('Error converting height:', error);
      return 'N/A';
    }
  };

  // Toggle expanded state for a player
  const toggleExpanded = (playerId: string) => {
    if (expandedPlayerId === playerId) {
      setExpandedPlayerId(null);
    } else {
      setExpandedPlayerId(playerId);
    }
  };

  // Calculate similarity breakdown for a player
  const calculateSimilarityBreakdown = (player: SimilarPlayer) => {
    if (!selectedPlayer) return [];

    const breakdown = [];

    // Position similarity
    const positionMatch = player.position === selectedPlayer.position;
    const otherPositionsMatch = player.otherPositions?.includes(selectedPlayer.position || '') || false;
    const positionScore = positionMatch 
      ? similarityCriteria.position 
      : (otherPositionsMatch ? similarityCriteria.position * 0.7 : 0);
    
    breakdown.push({
      criterion: 'Position',
      weight: similarityCriteria.position,
      score: positionScore,
      explanation: positionMatch 
        ? 'Exact position match' 
        : (otherPositionsMatch ? 'Position found in other positions' : 'Different position')
    });

    // Age similarity
    const ageDiff = Math.abs((player.age || 0) - (selectedPlayer.age || 0));
    const ageInRange = ageDiff <= similarityCriteria.age.range;
    const ageScore = ageInRange 
      ? similarityCriteria.age.weight 
      : similarityCriteria.age.weight * (1 - Math.min(ageDiff / similarityCriteria.age.range, 1));
    
    breakdown.push({
      criterion: 'Age',
      weight: similarityCriteria.age.weight,
      score: ageScore,
      explanation: ageInRange 
        ? `Within range (±${similarityCriteria.age.range} years)` 
        : `${ageDiff} years difference (outside range)`
    });

    // Height similarity
    const playerHeight = player.height_float || parseFloat((player.height || '0').replace('m', '').replace(',', '.'));
    const selectedPlayerHeight = selectedPlayer.height_float || parseFloat((selectedPlayer.height || '0').replace('m', '').replace(',', '.'));
    const heightDiff = Math.abs(playerHeight - selectedPlayerHeight) * 100; // Convert to cm
    const heightInRange = heightDiff <= similarityCriteria.height.range;
    const heightScore = heightInRange 
      ? similarityCriteria.height.weight 
      : similarityCriteria.height.weight * (1 - Math.min(heightDiff / similarityCriteria.height.range, 1));
    
    breakdown.push({
      criterion: 'Height',
      weight: similarityCriteria.height.weight,
      score: heightScore,
      explanation: heightInRange 
        ? `Within range (±${similarityCriteria.height.range} cm)` 
        : `${heightDiff.toFixed(1)} cm difference (outside range)`
    });

    // Market value similarity
    const playerMarketValue = player.market_value_float || 0;
    const selectedPlayerMarketValue = selectedPlayer.market_value_float || 0;
    const marketValueDiff = Math.abs(playerMarketValue - selectedPlayerMarketValue);
    const marketValueInRange = marketValueDiff <= similarityCriteria.marketValue.range;
    const marketValueScore = marketValueInRange 
      ? similarityCriteria.marketValue.weight 
      : similarityCriteria.marketValue.weight * (1 - Math.min(marketValueDiff / similarityCriteria.marketValue.range, 1));
    
    breakdown.push({
      criterion: 'Market Value',
      weight: similarityCriteria.marketValue.weight,
      score: marketValueScore,
      explanation: marketValueInRange 
        ? `Within range (±€${similarityCriteria.marketValue.range.toLocaleString()})` 
        : `€${marketValueDiff.toLocaleString()} difference (outside range)`
    });

    // Club information is no longer included in the similarity calculation or summary

    // Nationality similarity
    const nationalityMatch = player.nationality === selectedPlayer.nationality;
    const nationalityScore = nationalityMatch ? similarityCriteria.nationality : 0;
    
    breakdown.push({
      criterion: 'Nationality',
      weight: similarityCriteria.nationality,
      score: nationalityScore,
      explanation: nationalityMatch ? 'Same nationality' : 'Different nationality'
    });

    // Foot similarity
    const footMatch = player.foot === selectedPlayer.foot;
    const footScore = footMatch ? similarityCriteria.foot : 0;
    
    breakdown.push({
      criterion: 'Foot',
      weight: similarityCriteria.foot,
      score: footScore,
      explanation: footMatch ? 'Same preferred foot' : 'Different preferred foot'
    });

    // Goals similarity
    const playerGoals = player.goals || 0;
    const selectedPlayerGoals = selectedPlayer.goals || 0;
    let goalsScore = 0;
    let goalsExplanation = '';
    
    if (similarityCriteria.goals > 0) {
      if (selectedPlayerGoals === 0) {
        goalsScore = playerGoals === 0 ? similarityCriteria.goals : 0;
        goalsExplanation = playerGoals === 0 ? 'Both players have no goals' : `${playerGoals} goals vs 0 goals`;
      } else {
        const goalsDiff = Math.abs(playerGoals - selectedPlayerGoals);
        const goalsRatio = goalsDiff / Math.max(selectedPlayerGoals, 1);
        
        if (goalsRatio <= 0.2) {
          goalsScore = similarityCriteria.goals;
          goalsExplanation = `Similar goals (${playerGoals} vs ${selectedPlayerGoals})`;
        } else {
          goalsScore = similarityCriteria.goals * (1 - Math.min(goalsRatio, 1));
          goalsExplanation = `${playerGoals} goals vs ${selectedPlayerGoals} goals (${Math.round(goalsRatio * 100)}% difference)`;
        }
      }
      
      breakdown.push({
        criterion: 'Goals',
        weight: similarityCriteria.goals,
        score: goalsScore,
        explanation: goalsExplanation
      });
    }
    
    // Assists similarity
    const playerAssists = player.assists || 0;
    const selectedPlayerAssists = selectedPlayer.assists || 0;
    let assistsScore = 0;
    let assistsExplanation = '';
    
    if (similarityCriteria.assists > 0) {
      if (selectedPlayerAssists === 0) {
        assistsScore = playerAssists === 0 ? similarityCriteria.assists : 0;
        assistsExplanation = playerAssists === 0 ? 'Both players have no assists' : `${playerAssists} assists vs 0 assists`;
      } else {
        const assistsDiff = Math.abs(playerAssists - selectedPlayerAssists);
        const assistsRatio = assistsDiff / Math.max(selectedPlayerAssists, 1);
        
        if (assistsRatio <= 0.2) {
          assistsScore = similarityCriteria.assists;
          assistsExplanation = `Similar assists (${playerAssists} vs ${selectedPlayerAssists})`;
        } else {
          assistsScore = similarityCriteria.assists * (1 - Math.min(assistsRatio, 1));
          assistsExplanation = `${playerAssists} assists vs ${selectedPlayerAssists} assists (${Math.round(assistsRatio * 100)}% difference)`;
        }
      }
      
      breakdown.push({
        criterion: 'Assists',
        weight: similarityCriteria.assists,
        score: assistsScore,
        explanation: assistsExplanation
      });
    }
    
    // Minutes played similarity
    const playerMinutes = player.minutesPlayed || 0;
    const selectedPlayerMinutes = selectedPlayer.minutes_played || selectedPlayer.minutesPlayed || 0;
    let minutesScore = 0;
    let minutesExplanation = '';
    
    if (similarityCriteria.minutesPlayed.weight > 0) {
      if (selectedPlayerMinutes === 0) {
        minutesScore = playerMinutes === 0 ? similarityCriteria.minutesPlayed.weight : 0;
        minutesExplanation = playerMinutes === 0 ? 'Both players have no minutes played' : `${playerMinutes} minutes vs 0 minutes`;
      } else {
        const minRange = selectedPlayerMinutes * (1 - similarityCriteria.minutesPlayed.range / 100);
        const maxRange = selectedPlayerMinutes * (1 + similarityCriteria.minutesPlayed.range / 100);
        const inRange = playerMinutes >= minRange && playerMinutes <= maxRange;
        
        if (inRange) {
          minutesScore = similarityCriteria.minutesPlayed.weight;
          minutesExplanation = `Within range (±${similarityCriteria.minutesPlayed.range}%): ${playerMinutes.toLocaleString()} minutes`;
        } else {
          const minutesDiff = Math.abs(playerMinutes - selectedPlayerMinutes);
          const minutesRatio = minutesDiff / Math.max(selectedPlayerMinutes, 1);
          minutesScore = similarityCriteria.minutesPlayed.weight * (1 - Math.min(minutesRatio, 1));
          minutesExplanation = `${playerMinutes.toLocaleString()} minutes vs ${selectedPlayerMinutes.toLocaleString()} minutes (outside range)`;
        }
      }
      
      breakdown.push({
        criterion: 'Minutes Played',
        weight: similarityCriteria.minutesPlayed.weight,
        score: minutesScore,
        explanation: minutesExplanation
      });
    }

    return breakdown;
  };

  if (isSearchingSimilar) {
    return (
      <div className="mt-6 p-4 rounded-lg bg-[#26222E] text-[#CECFD2]">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#4E46FF] border-t-transparent"></div>
          <span>Searching for similar players...</span>
        </div>
      </div>
    );
  }

  if (error && !similarPlayers.length) {
    return (
      <div className="mt-6 p-4 rounded-lg bg-[#26222E] text-[#CECFD2]">
        <p>{error}</p>
      </div>
    );
  }

  // If we have a selected player and valid criteria but no search has been performed yet
  if (selectedPlayer && isCriteriaValid && !similarPlayers.length && !isSearchingSimilar && !error) {
    return (
      <div className="mt-6 p-4 rounded-lg bg-[#26222E] text-[#CECFD2]">
        <p>Click "Perform Similarity Search" to find similar players.</p>
      </div>
    );
  }

  if (!similarPlayers.length) {
    return null;
  }

  return (
    <div className="mt-6 bg-[#121212] rounded-lg p-4">
      <h2 className="text-xl font-semibold text-[#CECFD2] mb-3">Similar Players</h2>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-[#CECFD2]">
          <thead className="bg-[#26222E] text-xs">
            <tr>
              <th className="px-2 py-2 text-left font-medium">Player</th>
              <th className="px-2 py-2 text-left font-medium">Club</th>
              <th className="px-2 py-2 text-center font-medium">Age</th>
              <th className="px-2 py-2 text-center font-medium">Height</th>
              <th className="px-2 py-2 text-center font-medium">G/A</th>
              <th className="px-2 py-2 text-center font-medium">Value</th>
              <th className="px-2 py-2 text-center font-medium">Match</th>
              <th className="px-2 py-2 text-center font-medium">
                <span className="inline-flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  Details
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-500">
            {similarPlayers.map((player) => (
              <React.Fragment key={player.id}>
                <tr className="hover:bg-[#26222E]">
                  {/* Player Name & Position */}
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-2">
                      {player.imageUrl && (
                        <img 
                          src={player.imageUrl} 
                          alt={player.name}
                          className="w-8 h-8 object-cover rounded-full"
                        />
                      )}
                      <div>
                        <div className="font-medium text-[#CECFD2]">{player.name}</div>
                        <div className="text-xs text-[#CECFD2] opacity-75">{player.position || 'Unknown'}</div>
                      </div>
                    </div>
                  </td>
                  
                  {/* Club */}
                  <td className="px-2 py-2">
                    <div className="text-sm text-[#CECFD2]">{player.clubName || 'Unknown'}</div>
                    <div className="text-xs text-[#CECFD2] opacity-75">
                      {player.contractExpires ? `Until ${player.contractExpires}` : ''}
                    </div>
                  </td>
                  
                  {/* Age */}
                  <td className="px-2 py-2 text-center text-[#CECFD2]">{player.age || 'N/A'}</td>
                  
                  {/* Height */}
                  <td className="px-2 py-2 text-center text-[#CECFD2]">
                    {player.height ? player.height.replace(',', '.') : 'N/A'}
                  </td>
                  
                  {/* Goals/Assists */}
                  <td className="px-2 py-2 text-center">
                    <div className="flex justify-center gap-1 text-[#CECFD2]">
                      <span>{player.goals || 0}</span>
                      <span>/</span>
                      <span>{player.assists || 0}</span>
                    </div>
                  </td>
                  
                  {/* Market Value */}
                  <td className="px-2 py-2 text-center text-[#CECFD2]">
                    {player.marketValue ? player.marketValue.replace('€', '').replace('.00m', 'm') : 'N/A'}
                  </td>
                  
                  {/* Match Percentage */}
                  <td className="px-2 py-2 text-center">
                    <span className="inline-block px-2 py-1 bg-[#26222E] text-[#CECFD2] text-xs font-medium rounded-full">
                      {Math.round(player.similarityScore)}%
                    </span>
                  </td>
                  
                  {/* Details Button */}
                  <td className="px-2 py-2 text-center">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 w-6 p-0 border border-gray-500 bg-[#26222E] text-[#CECFD2]"
                      onClick={() => toggleExpanded(player.id)}
                      title={expandedPlayerId === player.id ? "Hide details" : "Show details"}
                    >
                      {expandedPlayerId === player.id ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </td>
                </tr>
                
                {/* Expanded Details Row */}
                {expandedPlayerId === player.id && (
                  <tr>
                    <td colSpan={8} className="p-0 bg-[#121212]">
                      <div className="p-4 text-sm text-[#CECFD2]">
                        <div className="flex flex-col md:flex-row gap-6">
                          {/* Player Image and Basic Info */}
                          <div className="flex flex-col items-center md:items-start md:flex-row gap-4">
                            {player.imageUrl && (
                              <img 
                                src={player.imageUrl} 
                                alt={player.name}
                                className="w-24 h-24 object-cover rounded-lg shadow-lg"
                              />
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="text-lg font-bold text-[#CECFD2]">{player.name}</h3>
                                <span className="px-2 py-1 bg-[#26222E] text-[#CECFD2] text-xs font-medium rounded-full">
                                  {Math.round(player.similarityScore)}% match
                                </span>
                              </div>
                              {player.fullName && <p className="text-sm text-[#CECFD2] opacity-75">{player.fullName}</p>}
                              <div className="mt-1 flex flex-wrap gap-2">
                                {player.position && (
                                  <span className="px-2 py-1 bg-[#26222E] text-[#CECFD2] text-xs rounded-md">
                                    {player.position}
                                  </span>
                                )}
                              </div>
                              {player.clubName && (
                                <div className="mt-2">
                                  <span className="px-2 py-1 bg-[#26222E] text-[#CECFD2] text-xs font-medium rounded-md inline-flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M10 2a8 8 0 100 16 8 8 0 000-16zM5.172 9.172a4 4 0 015.656 0 1 1 0 10-1.414 1.414 2 2 0 00-2.828 0 1 1 0 01-1.414-1.414zM10 12a2 2 0 100 4 2 2 0 000-4z" clipRule="evenodd" />
                                    </svg>
                                    {player.clubName}
                                    {player.contractExpires && (
                                      <span className="ml-1 opacity-75">• Until {player.contractExpires}</span>
                                    )}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Player Details */}
                          <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-3 mt-4 md:mt-0">
                            {/* Current Club - Highlighted but not part of similarity calculation */}
                            <div className="col-span-2 md:col-span-3 bg-[#26222E] p-2 rounded-lg mb-1">
                              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                                <div>
                                  <p className="text-xs text-[#CECFD2] font-medium">
                                    Current Club <span className="text-xs font-normal opacity-75">(not used in similarity)</span>
                                  </p>
                                  <p className="text-sm font-bold text-[#CECFD2]">{player.clubName || 'Unknown'}</p>
                                </div>
                                <div className="mt-1 md:mt-0">
                                  <p className="text-xs text-[#CECFD2] font-medium">Contract Until</p>
                                  <p className="text-sm font-bold text-[#CECFD2]">{player.contractExpires || 'Unknown'}</p>
                                </div>
                              </div>
                            </div>
                            
                            {/* Performance Stats - Highlighted */}
                            <div className="col-span-2 md:col-span-3 bg-[#26222E] p-2 rounded-lg mb-1">
                              <div className="grid grid-cols-3 gap-2">
                                <div>
                                  <p className="text-xs text-[#CECFD2] font-medium">Goals</p>
                                  <p className="text-sm font-bold text-[#CECFD2]">{player.goals ?? 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-[#CECFD2] font-medium">Assists</p>
                                  <p className="text-sm font-bold text-[#CECFD2]">{player.assists ?? 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-[#CECFD2] font-medium">Minutes Played</p>
                                  <p className="text-sm font-bold text-[#CECFD2]">
                                    {player.minutesPlayed ? `${player.minutesPlayed.toLocaleString()}` : 'N/A'}
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            <div>
                              <p className="text-xs text-[#CECFD2] opacity-75">Age</p>
                              <p className="text-sm font-medium text-[#CECFD2]">{player.age || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-[#CECFD2] opacity-75">Nationality</p>
                              <p className="text-sm font-medium text-[#CECFD2]">{player.nationality || 'Unknown'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-[#CECFD2] opacity-75">Height</p>
                              <p className="text-sm font-medium text-[#CECFD2]">
                                {player.height ? (
                                  <>
                                    {player.height.replace(',', '.')} ({metersToFeetAndInches(player.height)})
                                  </>
                                ) : (
                                  'Not available'
                                )}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-[#CECFD2] opacity-75">Foot</p>
                              <p className="text-sm font-medium text-[#CECFD2]">{player.foot || 'Unknown'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-[#CECFD2] opacity-75">Market Value</p>
                              <p className="text-sm font-medium text-[#CECFD2]">{player.marketValue || 'Unknown'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-[#CECFD2] opacity-75">Position</p>
                              <p className="text-sm font-medium text-[#CECFD2]">{player.position || 'Unknown'}</p>
                            </div>
                          </div>
                        </div>

                        {/* Similarity Score Breakdown */}
                        <div className="mt-6 p-4 bg-[#26222E] rounded-lg">
                          <h4 className="text-sm font-bold text-[#CECFD2] mb-3">Similarity Score Breakdown</h4>
                          <p className="text-xs text-[#CECFD2] mb-3">
                            The similarity score is calculated based on the criteria weights you selected. 
                            Here's how {player.name}'s score of {Math.round(player.similarityScore)}% was calculated:
                          </p>
                          
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs text-[#CECFD2]">
                              <thead className="bg-[#121212]">
                                <tr>
                                  <th className="px-3 py-2 text-left">Criterion</th>
                                  <th className="px-3 py-2 text-center">Weight</th>
                                  <th className="px-3 py-2 text-center">Score</th>
                                  <th className="px-3 py-2 text-left">Explanation</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-500">
                                {calculateSimilarityBreakdown(player).map((item, index) => (
                                  <tr key={index} className="hover:bg-[#121212]">
                                    <td className="px-3 py-2 font-medium">{item.criterion}</td>
                                    <td className="px-3 py-2 text-center">{item.weight}%</td>
                                    <td className="px-3 py-2 text-center">
                                      {item.score.toFixed(1)}%
                                      <div className="w-full bg-[#121212] h-1 mt-1 rounded-full overflow-hidden">
                                        <div 
                                          className="bg-[#4E46FF] h-1 rounded-full" 
                                          style={{ width: `${(item.score / item.weight) * 100}%` }}
                                        ></div>
                                      </div>
                                    </td>
                                    <td className="px-3 py-2">{item.explanation}</td>
                                  </tr>
                                ))}
                                <tr className="bg-[#121212] font-medium">
                                  <td className="px-3 py-2">Total</td>
                                  <td className="px-3 py-2 text-center">100%</td>
                                  <td className="px-3 py-2 text-center">{Math.round(player.similarityScore)}%</td>
                                  <td className="px-3 py-2"></td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          <div className="mt-4 text-xs text-[#CECFD2]">
                            <p className="flex items-start gap-1">
                              <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              <span>
                                The similarity calculation compares each attribute of {player.name} with {selectedPlayer?.name}. 
                                For range-based criteria (age, height, market value), the score decreases proportionally as the difference increases beyond the specified range.
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};